import config from 'js/app/config';

import type { TestimonialsFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';

import _t from 'i18n!nls/front-page';

// These "kinds" match up to Contentful field names
export const VALUE_PROP_KINDS = {
  valuePropAccessible: 'accessible',
  valuePropAppliedLearning: 'appliedLearning',
  valuePropTopQuality: 'topQuality',
  valuePropCertificates: 'certificates',
};

export type ValuePropKind = (typeof VALUE_PROP_KINDS)[keyof typeof VALUE_PROP_KINDS];

export const HERO_BANNER_IMAGE = `${config.url.resource_assets}front-page-story/hero-banner/robot-girl.jpg`;

export const PROMO_STAT_IMG1_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/promoStat.png`;
export const PINKISH_HALF_CIRCLE_SVG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/pinkish-half-circle.svg`;
export const BLUE_DOTTED_BLOB_PNG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/blue-dotted-blob-half.png`;
export const LEARN_SVG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/SvgaLearn.svg`;
export const TEACHER_SVG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/teacher-img.svg`;
export const DEGREE_SVG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/SvgaColorCertificate.svg`;
export const COMMUNITY_SVG_SRC = `${config.url.resource_assets}front-page-story/achieve-your-goals/SvgaCommunity.svg`;

// Redesign Images
export const HERO_B_IMAGE = `${config.url.resource_assets}front-page-rebrand/hero/hero-b.png`;
export const HERO_C_IMAGE = `${config.url.resource_assets}front-page-rebrand/hero/hero-c-2.png`;
export const LEARNER_OUTCOMES_IMG_SRC = `${config.url.resource_assets}front-page-rebrand/learner-outcomes/outcomes.png`;

export const SECONDARY_CONSUMER_REBRAND_IMG_SRC = `${config.url.resource_assets}front-page-rebrand/secondary-consumer-cta/Image-Next-Step.png`;

export const UPSKILL_ORG_SVG = `${config.url.resource_assets}front-page-rebrand/achieve-your-goals/Icon-Upskill-your-org.svg`;
export const EARN_DEGREE_SVG = `${config.url.resource_assets}front-page-rebrand/achieve-your-goals/Icon-Earn-Degree.svg`;
export const GET_READY_SVG = `${config.url.resource_assets}front-page-rebrand/achieve-your-goals/Icon-Get-Ready.svg`;
export const REBRAND_LEARN_SVG = `${config.url.resource_assets}front-page-rebrand/achieve-your-goals/Icon-Learn.svg`;
export const AUTH_MODAL_LOGO_SVG =
  'https://d2j5ihb19pt1hq.cloudfront.net/front-page-rebrand/auth-modal/coursera-logo-white-rgb.svg';

export const BG_C_SVG = 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-rebrand/c.svg';

export const HERO_MODULE_V2_IMAGE = `${config.url.resource_assets}front-page/lohp/lohp-hero-v2-banner.png`;

export const getLOHPTestimonialsHeader = () => ({
  title: _t('From the Coursera community'),
  subtitle: _t('124+ million people have already joined Coursera'),
});

const getEnterprisePartners = () => ({
  AXA: {
    id: 'axa',
    title: _t('AXA'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/axa.svg',
    logoIsSvg: true,
  },
  AXIS_BANK: {
    id: 'axisbank',
    title: _t('Axis Bank'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/axisbank.svg',
    logoIsSvg: true,
  },
  ACCELYA: {
    id: 'accelya',
    title: _t('Accelya'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/accelya.svg',
    logoIsSvg: true,
  },
  DANONE: {
    id: 'danone',
    title: _t('Danone'),
    imgSrc: `${config.url.resource_assets}front-page-story/enterprise-partners/danone.png`,
    logoIsSvg: false,
  },
  SK: {
    id: 'sk',
    title: _t('SK Telecom'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/sk.svg',
    logoIsSvg: true,
  },
  TATACOMM: {
    id: 'tatacomm',
    title: _t('Tata Communications'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/tata.svg',
    logoIsSvg: true,
  },
  AIRBUS: {
    id: 'airbus',
    title: _t('Airbus'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/airbus.svg',
    logoIsSvg: true,
  },
  PANDG: {
    id: 'pandg',
    title: _t('Procter & Gamble'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/pandg.svg',
    logoIsSvg: true,
  },
  LOREAL: {
    id: 'loreal',
    title: _t("L'OREAL"),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/loreal.svg',
    logoIsSvg: true,
  },
  NOVARTIS: {
    id: 'novartis',
    title: _t('Novartis'),
    imgSrc: 'https://d2j5ihb19pt1hq.cloudfront.net/front-page-story/enterprise-partners/novartis.svg',
    logoIsSvg: true,
  },
  TELENOR: {
    id: 'telenor',
    title: _t('Telenor'),
    imgSrc: `${config.url.resource_assets}front-page-story/enterprise-partners/telenor.png`,
    logoIsSvg: false,
  },
});

export const enterprisePartnerLogoDimensions: Record<string, { maxWidth: number; maxHeight: number }> = {
  LOREAL: {
    maxWidth: 100,
    maxHeight: 18,
  },
  PANDG: {
    maxWidth: 40,
    maxHeight: 40,
  },
  NOVARTIS: {
    maxWidth: 140,
    maxHeight: 22,
  },
  TELENOR: {
    maxWidth: 90,
    maxHeight: 40,
  },
  AIRBUS: {
    maxWidth: 105,
    maxHeight: 19,
  },
  AXIS_BANK: {
    maxWidth: 110,
    maxHeight: 30,
  },
  ACCELYA: {
    maxWidth: 90,
    maxHeight: 25,
  },
  TATACOMM: {
    maxWidth: 40,
    maxHeight: 37,
  },
  AXA: {
    maxWidth: 35,
    maxHeight: 35,
  },
  DANONE: {
    maxWidth: 110,
    maxHeight: 36,
  },
  SK: {
    maxWidth: 35,
    maxHeight: 27,
  },
  DEFAULT: {
    maxHeight: 30,
    maxWidth: 90,
  },
};

export const getEnterprisePartnerLogos = () => [
  {
    ...getEnterprisePartners().LOREAL,
    ...enterprisePartnerLogoDimensions.LOREAL,
  },
  {
    ...getEnterprisePartners().PANDG,
    ...enterprisePartnerLogoDimensions.PANDG,
  },
  {
    ...getEnterprisePartners().NOVARTIS,
    ...enterprisePartnerLogoDimensions.NOVARTIS,
  },
  {
    ...getEnterprisePartners().TELENOR,
    ...enterprisePartnerLogoDimensions.TELENOR,
  },
  {
    ...getEnterprisePartners().AIRBUS,
    ...enterprisePartnerLogoDimensions.AIRBUS,
  },
  {
    ...getEnterprisePartners().AXIS_BANK,
    ...enterprisePartnerLogoDimensions.AXIS_BANK,
  },
  {
    ...getEnterprisePartners().ACCELYA,
    ...enterprisePartnerLogoDimensions.ACCELYA,
  },
  {
    ...getEnterprisePartners().TATACOMM,
    ...enterprisePartnerLogoDimensions.TATACOMM,
  },
  {
    ...getEnterprisePartners().AXA,
    ...enterprisePartnerLogoDimensions.AXA,
  },
  {
    ...getEnterprisePartners().DANONE,
    ...enterprisePartnerLogoDimensions.DANONE,
  },
  {
    ...getEnterprisePartners().SK,
    ...enterprisePartnerLogoDimensions.SK,
  },
];

export const partnerLogoHeights: Record<string, number> = {
  illinois: 32,
  duke: 32,
  google: 37,
  umich: 55,
  ibm: 32,
  imperial: 35,
  stanford: 27,
  penn: 37,
  default: 37,
};

export const getPartnerLogos = () => [
  {
    relationSlug: 'illinois',
    imageAltText: _t('University of Illinois at Urbana-Champaign'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/illinois.svg` },
  },
  {
    relationSlug: 'duke',
    imageAltText: _t('Duke University'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/duke_updated_2020.png` },
  },
  {
    relationSlug: 'google',
    imageAltText: _t('Google'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/google.png` },
  },
  {
    relationSlug: 'umich',
    imageAltText: _t('University of Michigan'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/umich.png` },
  },
  {
    relationSlug: 'ibm-skills-network',
    imageAltText: _t('IBM'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/ibm.png` },
  },
  {
    relationSlug: 'imperial',
    imageAltText: _t('Imperial College of London'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/imperial.png` },
  },
  {
    relationSlug: 'stanford',
    imageAltText: _t('Stanford University'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/stanford.svg` },
  },
  {
    relationSlug: 'penn',
    imageAltText: _t('University of Pennsylvania'),
    image: { url: `${config.url.resource_assets}front-page-story/partners/penn.svg` },
  },
];

export const SECONDARY_CONSUMER_IMG_SRC = `${config.url.resource_assets}front-page-story/secondary-consumer-cta/secondary-consumer-hero-img.png`;

export const RED_DOTTED_HALF_CIRCLE_SRC = `${config.url.resource_assets}front-page-story/secondary-consumer-cta/red-dotted-half-circle.png`;

export const getPartnerCTAData = () => ({
  href: '/about/partners',
  className: 'see-all-link',
  trackingName: 'see_all_partner',
  data: { value: 'showAllPartners' },
  message: _t(`We collaborate with <span class="underline">350+ leading universities and companies</span>`),
});

export const getFallbackSocialProofs = (): Array<TestimonialsFieldsFragment> => [
  {
    authorName: _t('Julio R.'),
    authorTitle: _t('Web Developer'),
    authorOrganization: _t('Chile'),
    text: _t(
      '"I started at stage zero. With Coursera I was able to start learning online and eventually build up enough knowledge and skills to transition into a well-paying career."'
    ),
    image: {
      url: `${config.url.resource_assets}front-page-rebrand/social-proof/coursera-learner-a.png`,
    },
  },
  {
    authorName: _t('Kara A.'),
    authorTitle: _t('iMBA Graduate, University of Illinois Gies College of Business'),
    authorOrganization: _t('United States'),
    text: _t(
      '"It’s a really big deal to offer an online MBA that’s a real MBA at a price that’s achievable. This program is totally disrupting higher education."'
    ),
    image: {
      url: `${config.url.resource_assets}front-page-rebrand/social-proof/coursera-learner-b.png`,
    },
  },
  {
    authorName: _t('Mirela I.'),
    authorTitle: _t('IBM Data Science Professional Certificate Alumna'),
    authorOrganization: _t('Romania'),
    text: _t(
      '"Recruiters saw my Professional Certificate on my LinkedIn profile. During the interview, they told me they were impressed with the skills I learned. I got the job!"'
    ),
    image: {
      url: `${config.url.resource_assets}front-page-rebrand/social-proof/coursera-learner-c.png`,
    },
  },
  {
    authorName: _t('Sabrina M.'),
    authorTitle: _t('HR Manager, ZS Associates'),
    authorOrganization: _t('United States'),
    text: _t(
      '"We were looking for scale, credibility, and something we didn’t have to build in-house. Coursera not only offers training from very prestigious institutions but also a platform that allows you to do the tracking that you want."'
    ),
    image: {
      url: `${config.url.resource_assets}front-page-rebrand/social-proof/coursera-learner-d.png`,
    },
  },
];
